import dayjs, { type Dayjs } from "dayjs";
import type {
  DataLockAction,
  DataLockAttribute,
  DataLockCostLine,
  DataLockFeature,
  DataLockMutationPayload,
  DataLockRecord,
} from "../../services/domains/data-lock/types";
import type {
  DataLockAttributeFormRow,
  DataLockFormOption,
  DataLockFormValidationErrors,
  DataLockFormValues,
} from "./types";

const CALENDAR_DATE_INPUT =
  /^(\d{4}-\d{2}-\d{2})(?:(?:T| )(?:[01]\d|2[0-3]):[0-5]\d(?::[0-5]\d(?:\.\d+)?)?(?:Z|[+-](?:(?:0\d|1[0-3]):[0-5]\d|14:00))?)?$/;
const DATA_LOCK_FEATURES: readonly DataLockFeature[] = [
  "ORDER",
  "ORDER_COST_SHEET",
];

const VALIDATION_MESSAGES = {
  name: "Name is required",
  feature: "Feature is required",
  from: "From date is required",
  to: "To date is required",
  actions: "At least one action is required",
  dateRange: "From date must be on or before to date",
  attributeField: "Every attribute row must select a field",
  duplicateAttributeField:
    "Attribute fields must be unique within each scope",
} as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isDataLockFeature = (value: unknown): value is DataLockFeature =>
  typeof value === "string" &&
  DATA_LOCK_FEATURES.includes(value as DataLockFeature);

const isWildcardMarker = (value: unknown): value is string =>
  typeof value === "string" && value.trim() === "*";

export const normalizeDataLockActions = (
  values: unknown,
): DataLockAction[] => {
  if (!Array.isArray(values)) return [];

  const seen = new Set<string>();
  return values.reduce<DataLockAction[]>((result, value) => {
    const code = typeof value === "string" ? value.trim() : "";
    if (!code || seen.has(code)) return result;

    seen.add(code);
    result.push(code);
    return result;
  }, []);
};

const parseCalendarDate = (value: unknown): Dayjs | null => {
  if (typeof value !== "string") {
    return null;
  }

  const calendarDate = value.match(CALENDAR_DATE_INPUT)?.[1];
  if (!calendarDate) {
    return null;
  }

  const parsed = dayjs(calendarDate);
  return parsed.isValid() && parsed.format("YYYY-MM-DD") === calendarDate
    ? parsed
    : null;
};

const getDisplayLabel = (value: Record<string, unknown>, fallback: string) => {
  for (const key of ["label", "name", "code"] as const) {
    const label = value[key];
    if (typeof label === "string" && label.length > 0) {
      return label;
    }
  }

  return fallback;
};

const findPopulatedValue = (
  populatedValues: unknown[],
  key: "value" | "_id",
  value: string,
): Record<string, unknown> | undefined =>
  populatedValues.find(
    (candidate): candidate is Record<string, unknown> =>
      isRecord(candidate) && candidate[key] === value,
  );

const hydrateSelectedOptions = (
  values: string[],
  populatedValues: unknown,
): DataLockFormOption[] => {
  const safePopulatedValues = Array.isArray(populatedValues)
    ? populatedValues
    : [];

  return values.map((value) => {
    const populatedValue =
      findPopulatedValue(safePopulatedValues, "value", value) ??
      findPopulatedValue(safePopulatedValues, "_id", value);

    return {
      value,
      label: populatedValue
        ? getDisplayLabel(populatedValue, value)
        : value,
    };
  });
};

const hydrateAttributes = (
  attributes: unknown,
  keyPrefix: "restricted" | "whitelisted",
): DataLockAttributeFormRow[] => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes.flatMap((attribute, index) => {
    if (
      !isRecord(attribute) ||
      typeof attribute.field !== "string" ||
      attribute.field.trim().length === 0 ||
      isWildcardMarker(attribute.field) ||
      !Array.isArray(attribute.values)
    ) {
      return [];
    }

    const stringValues = attribute.values.filter(
      (value): value is string => typeof value === "string",
    );
    const allValues =
      stringValues.length === 0 ||
      stringValues.some(isWildcardMarker);
    const values = allValues
      ? []
      : stringValues;

    return [
      {
        key: `${keyPrefix}-${index}`,
        field: attribute.field,
        allValues,
        values: allValues ? [] : values,
        selectedOptions: allValues
          ? []
          : hydrateSelectedOptions(values, attribute.populatedValues),
      },
    ];
  });
};

const hydrateCostGroups = (groups: unknown): string[] =>
  Array.isArray(groups) && !groups.some(isWildcardMarker)
    ? groups.filter((group): group is string => typeof group === "string")
    : [];

const isCostLine = (line: unknown): line is DataLockCostLine =>
  isRecord(line) &&
  typeof line.configId === "string" &&
  typeof line.code === "string" &&
  typeof line.name === "string";

const isWildcardCostLine = (line: unknown) =>
  isWildcardMarker(line) ||
  (isRecord(line) &&
    ["configId", "code", "name"].some((key) =>
      isWildcardMarker(line[key]),
    ));

const hydrateCostLines = (lines: unknown): DataLockCostLine[] =>
  Array.isArray(lines) && !lines.some(isWildcardCostLine)
    ? lines.filter(isCostLine).map(({ configId, code, name }) => ({
        configId,
        code,
        name,
      }))
    : [];

export const createEmptyDataLockForm = (): DataLockFormValues => ({
  name: "",
  description: "",
  feature: undefined,
  from: null,
  to: null,
  actions: [],
  active: true,
  restrictedAttributes: [],
  whitelistedAttributes: [],
  restrictedGroups: [],
  restrictedLines: [],
});

export const dataLockDetailToForm = (
  record: DataLockRecord,
): DataLockFormValues => {
  if (!isRecord(record)) {
    return createEmptyDataLockForm();
  }

  const orderScope: Record<string, unknown> = isRecord(record.orderScope)
    ? record.orderScope
    : {};
  const costSheetScope: Record<string, unknown> = isRecord(
    record.costSheetScope,
  )
    ? record.costSheetScope
    : {};

  return {
    name: typeof record.name === "string" ? record.name : "",
    description:
      typeof record.description === "string" ? record.description : "",
    feature: isDataLockFeature(record.feature) ? record.feature : undefined,
    from: parseCalendarDate(record.from),
    to: parseCalendarDate(record.to),
    actions: normalizeDataLockActions(record.actions),
    active: typeof record.active === "boolean" ? record.active : true,
    restrictedAttributes: hydrateAttributes(
      orderScope.restrictedAttributes,
      "restricted",
    ),
    whitelistedAttributes: hydrateAttributes(
      orderScope.whitelistedAttributes,
      "whitelisted",
    ),
    restrictedGroups: hydrateCostGroups(costSheetScope.restrictedGroups),
    restrictedLines: hydrateCostLines(costSheetScope.restrictedLines),
  };
};

export const resetFeatureSpecificForm = (
  values: DataLockFormValues,
  nextFeature: DataLockFeature,
): DataLockFormValues => ({
  ...values,
  feature: nextFeature,
  actions: [],
  restrictedAttributes: [],
  whitelistedAttributes: [],
  restrictedGroups: [],
  restrictedLines: [],
});

const isValidFormDate = (value: Dayjs | null): value is Dayjs =>
  dayjs.isDayjs(value) && value.isValid();

const hasMissingAttributeField = (attributes: unknown) =>
  Array.isArray(attributes) &&
  attributes.some(
    (attribute) =>
      !isRecord(attribute) ||
      typeof attribute.field !== "string" ||
      attribute.field.trim().length === 0,
  );

const hasDuplicateAttributeField = (attributes: unknown) => {
  if (!Array.isArray(attributes)) return false;

  const seen = new Set<string>();
  return attributes.some((attribute) => {
    if (!isRecord(attribute) || typeof attribute.field !== "string") {
      return false;
    }

    const field = attribute.field.trim();
    if (!field) return false;
    if (seen.has(field)) return true;

    seen.add(field);
    return false;
  });
};

export const validateDataLockForm = (
  values: DataLockFormValues,
): DataLockFormValidationErrors => {
  const errors: DataLockFormValidationErrors = {};
  const { from, to } = values;
  const validFrom = isValidFormDate(from);
  const validTo = isValidFormDate(to);

  if (typeof values.name !== "string" || values.name.trim().length === 0) {
    errors.name = VALIDATION_MESSAGES.name;
  }
  if (!isDataLockFeature(values.feature)) {
    errors.feature = VALIDATION_MESSAGES.feature;
  }
  if (!validFrom) {
    errors.from = VALIDATION_MESSAGES.from;
  }
  if (!validTo) {
    errors.to = VALIDATION_MESSAGES.to;
  }
  if (normalizeDataLockActions(values.actions).length === 0) {
    errors.actions = VALIDATION_MESSAGES.actions;
  }
  if (
    validFrom &&
    validTo &&
    from.startOf("day").isAfter(to.startOf("day"))
  ) {
    errors.dateRange = VALIDATION_MESSAGES.dateRange;
  }
  if (values.feature === "ORDER") {
    if (
      hasMissingAttributeField(values.restrictedAttributes) ||
      hasMissingAttributeField(values.whitelistedAttributes)
    ) {
      errors.attributeField = VALIDATION_MESSAGES.attributeField;
    }
    if (
      hasDuplicateAttributeField(values.restrictedAttributes) ||
      hasDuplicateAttributeField(values.whitelistedAttributes)
    ) {
      errors.duplicateAttributeField =
        VALIDATION_MESSAGES.duplicateAttributeField;
    }
  }

  return errors;
};

const serializeAttributes = (
  attributes: DataLockAttributeFormRow[],
): DataLockAttribute[] => {
  const seen = new Set<string>();

  return attributes.map((attribute) => {
    const field =
      typeof attribute.field === "string" ? attribute.field.trim() : "";
    if (!field || isWildcardMarker(field)) {
      throw new Error("Data lock attribute field is required");
    }
    if (seen.has(field)) {
      throw new Error(
        "Data lock attribute fields must be unique within each scope",
      );
    }
    seen.add(field);

    const values = Array.isArray(attribute.values)
      ? attribute.values
      : [];
    return {
      field,
      values:
        attribute.allValues || values.some(isWildcardMarker)
          ? []
          : values.filter(
              (value): value is string => typeof value === "string",
            ),
    };
  });
};

const serializeCostGroups = (groups: unknown): string[] =>
  Array.isArray(groups) && !groups.some(isWildcardMarker)
    ? groups.filter((group): group is string => typeof group === "string")
    : [];

const serializeCostLines = (lines: unknown): DataLockCostLine[] =>
  Array.isArray(lines) && !lines.some(isWildcardCostLine)
    ? lines.filter(isCostLine).map(({ configId, code, name }) => ({
        configId,
        code,
        name,
      }))
    : [];

export const buildDataLockPayload = (
  values: DataLockFormValues,
): DataLockMutationPayload => {
  if (!isDataLockFeature(values.feature)) {
    throw new Error("Data lock feature is required");
  }
  if (!isValidFormDate(values.from)) {
    throw new Error("Data lock from date is required");
  }
  if (!isValidFormDate(values.to)) {
    throw new Error("Data lock to date is required");
  }

  const common = {
    name: values.name.trim(),
    description: values.description.trim(),
    actions: normalizeDataLockActions(values.actions),
    active: values.active,
    from: values.from.format("YYYY-MM-DD"),
    to: values.to.format("YYYY-MM-DD"),
  };

  if (values.feature === "ORDER") {
    return {
      ...common,
      feature: "ORDER",
      orderScope: {
        restrictedAttributes: serializeAttributes(
          values.restrictedAttributes,
        ),
        whitelistedAttributes: serializeAttributes(
          values.whitelistedAttributes,
        ),
      },
    };
  }

  return {
    ...common,
    feature: "ORDER_COST_SHEET",
    costSheetScope: {
      restrictedGroups: serializeCostGroups(values.restrictedGroups),
      restrictedLines: serializeCostLines(values.restrictedLines),
    },
  };
};
